import { inject, Injectable } from '@angular/core';
import { deleteField, DocumentReference, getDocs, query, updateDoc, where, UpdateData, FieldValue } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  BOIType,
  cloudFunctionNames,
  IDocumentType,
  IEntity,
  IEntityBoirStatusUpdate,
  IEntityDocument,
  IEntityMap,
  IEntitySubmitConsent,
  IHttpsCallableResponse,
  IOwner,
  isHandedOffEntity,
  IShareType,
  IShareTypeReference,
  isProEntity,
  UUID
} from '../../core/interfaces';
import { EntityService, IEntityOperation, IEntityService } from '../../core/services/entity.service';
import { OrganizationService } from './organization.service';
import { collection, documentId } from "@angular/fire/firestore";
import { combineLatest, distinctUntilChanged, Observable, switchMap } from 'rxjs';
import {
  IShareTypeChanges
} from "../../modules/entities/components/cap-table/components/configure-share-types-dialog/configure-share-types-dialog.component";
import {
  IShareRecordChanges
} from "../../modules/entities/components/cap-table/components/configure-share-records-dialog/configure-share-records-dialog.component";
import { chunkArray, exhaustiveCheck } from "@cloud-functions/utils";
import {
  IEntityCf,
  IRevokeSecureFileInviteReq,
  ISendInstantlyTransferEmailReq,
  ISendSecureFileTransferEmailReq
} from "@cloud-functions/interfaces";

type IProEntityOperation = keyof Pick<ISecureProService,
  | "addOwnerToEntity"
  | "removeOwnerFromEntity"
  | "removeOwnerFromOrg"
  | "transferEntityToSecureFile"
  | "instantlyTransferEntityToSecureFile"
  | "updateShareTypes"
  | "manageCapTableRecords"
> | IEntityOperation;

type IProEntityOperationFunction<T extends IProEntityOperation> = (
  ...args: Parameters<ISecureProService[T]>
) => Promise<Awaited<ReturnType<ISecureProService[T]>>>;

interface ISecureProService extends IEntityService {
  getEntities(groupIds: Set<UUID>, orgId: UUID): Promise<IEntityMap>;
  getEntitiesByIds(orgId: UUID, entityIds: Set<UUID>): Promise<IEntity[]>
  // group-related methods
  addEntityToGroup(orgId: UUID, entityId: UUID, entityLegalName: string, groupId: UUID): Promise<IEntity>;
  moveEntityToGroup(orgId: UUID, entityId: UUID, entityLegalName: string, fromGroupId: UUID, toGroupId: UUID): Promise<IEntity>;
  // owner-related methods
  getEntityOwners(orgId: UUID, entityId: UUID): Promise<IOwner[]>;
  getEntitiesByOwnerId(orgId: UUID, ownerId: UUID): Promise<IEntity[]>;
  addOwnerToEntity(orgId: UUID, entityId: UUID, ownerId: UUID, boiType: BOIType): Promise<IEntity>;
  removeOwnerFromEntity(orgId: UUID, entityId: UUID, ownerId: UUID, boiType: BOIType): Promise<IEntity>;
  removeOwnerFromOrg(orgId: UUID, ownerId: UUID): Promise<IEntity[]>;
  // file-related methods
  transferEntityToSecureFile(entityId: UUID, orgId: UUID, ownerId: UUID): Promise<IEntity>;
  instantlyTransferEntityToSecureFile(entityId: UUID, orgId: UUID, userId: UUID, email: string, displayName: string): Promise<IEntity>;
  revokeSecureFileInvitation(entityId: UUID, orgId: UUID): Promise<IEntity>;
  // fincen-related methods
  submitToFinCEN(entityId: UUID, orgId: UUID, consentData: IEntitySubmitConsent): Promise<IEntity>;
  updateEntityBoirStatus(entityId: UUID, orgId: UUID, entityBoirStatusUpdate: IEntityBoirStatusUpdate): Promise<void>
  // capitalization table related methods
  updateShareTypes(orgId: UUID, entityId: UUID, shareTypeChanges: IShareTypeChanges): Promise<IEntity>
  manageCapTableRecords(orgId: UUID, entityId: UUID, shareTypeId: UUID, shareRecordChanges: IShareRecordChanges): Promise<IEntity>
  // method curring for entity operations to handle entity mirroring
  createEntityOperation<T extends IProEntityOperation>(operation: T): IProEntityOperationFunction<T>;
}

@Injectable({
  providedIn: 'root'
})
export class SecureProService extends EntityService implements ISecureProService {
  private orgService: OrganizationService = inject(OrganizationService);

  constructor() {
    super("secure-pro");
  }

  public createEntityOperation<T extends IProEntityOperation>(operation: T) {
    return async (...args: Parameters<ISecureProService[T]>): Promise<Awaited<ReturnType<ISecureProService[T]>>> => {
      try {
        const method = this[operation] as IProEntityOperationFunction<T>;
        const result = await method.apply(this, args);
        try {
          await this.interceptor(operation, args, result);
        } catch (error: unknown) {
          console.error("Error in entity operation interceptor", operation, error);
          throw error;
        }
        return result;
      } catch (error: unknown) {
        console.error("Error in entity operation", operation, error);
        throw error;
      }
    }
  }

  private async interceptor(
    operation: IProEntityOperation,
    args: Parameters<ISecureProService[typeof operation]>,
    result: Awaited<ReturnType<ISecureProService[typeof operation]>>
  ): Promise<void> {
    switch (operation) {
      case "createEntity":
      case "updateEntity":
      case "addOwnerToEntity":
      case "removeOwnerFromEntity":
      case "updateShareTypes":
      case "manageCapTableRecords": {
        const entity = result as Awaited<ReturnType<ISecureProService[typeof operation]>>;
        const mirrorPromises: Promise<void>[] = [];
        if (!isProEntity(entity)) {
          throw new Error("Invalid entity type: expected pro entity");
        }
        if (operation === "updateEntity") {
          mirrorPromises.push(
            this.groupService.mirrorEntityToGroup(entity.id, entity.legalName, entity.managingOrgId, entity.groupId)
          );
        }
        if (isHandedOffEntity(entity)) {
          mirrorPromises.push(
            this.mirrorEntityFromUser(entity.id, entity.managingOrgId)
          );
        }
        await Promise.all(mirrorPromises);
        break;
      }
      case "deleteEntity": {
        const entity = result as Awaited<ReturnType<ISecureProService[typeof operation]>>;
        if (!isProEntity(entity)) {
          throw new Error("Invalid entity type: expected pro entity");
        }
        await this.groupService.removeEntityFromGroup(
          entity.managingOrgId,
          entity.id,
          entity.legalName,
          entity.groupId
        );
        await this.entityDetailsService.deleteEntityDetails(entity.id);

        if (isHandedOffEntity(entity)) {
          await this.mirrorDeleteEntityFromUser(entity.id);
        }
        break;
      }
      case "removeOwnerFromOrg": {
        const entities = result as Awaited<ReturnType<ISecureProService[typeof operation]>>
        if (!entities.every(isHandedOffEntity)) {
          console.error("Invalid entity type, expected handed-off entity");
          return Promise.resolve();
        }
        await Promise.all(entities.map(
          entity => this.mirrorEntityFromUser(entity.id, entity.managingOrgId))
        );
        break;
      }
      case "getTotalEntityProgress": {
        const [entityId, orgId] = args as Parameters<ISecureProService[typeof operation]>;
        if (!orgId) {
          throw new Error("Organization ID is required in getTotalEntityProgress for pro entity");
        }
        const totalProgress = result as Awaited<ReturnType<ISecureProService[typeof operation]>>;
        const entityRef = this.getProEntityRef(orgId, entityId);

        await updateDoc(entityRef, {
          progress: totalProgress.totalPercentFilled
        });

        const entity = await this.getProEntity(orgId, entityId);
        if (isHandedOffEntity(entity)) {
          await this.mirrorEntityProgressFromUser(entity.id, orgId);
        }
        break;
      }
      case "transferEntityToSecureFile": {
        const [entityId, orgId, ownerId] = args as Parameters<ISecureProService[typeof operation]>;
        const entity = result as Awaited<ReturnType<ISecureProService[typeof operation]>>;

        if (!isHandedOffEntity(entity)) {
          throw new Error("Invalid entity type, expected handed-off entity");
        }

        const {firstName, lastName, email} = await this.ownerService.getById(ownerId);

        if (!email) {
          throw new Error("Owner does not have an email!");
        }

        const org = await this.orgService.getOrg(orgId);

        const sendInvitationEmailCallable = httpsCallable<
          ISendSecureFileTransferEmailReq,
          IHttpsCallableResponse
        >(this.functions, cloudFunctionNames.secureFileTransfer.sendInvitationEmail);

        const response = await sendInvitationEmailCallable({
          email,
          ownerName: `${firstName} ${lastName}`,
          entityId,
          entityName: entity.legalName,
          orgId,
          orgName: org.name,
        });

        if (!response.data.success) {
          throw new Error(response.data.message);
        }
        break;
      }
      case "instantlyTransferEntityToSecureFile": {
        const [entityId, orgId, /* userId */, email, displayName] =
          args as Parameters<ISecureProService[typeof operation]>;
        const entity = result as Awaited<ReturnType<ISecureProService[typeof operation]>>;

        if (!isHandedOffEntity(entity)) {
          throw new Error("Invalid entity type, expected handed-off entity");
        }

        if (!email) {
          throw new Error("Owner does not have an email!");
        }

        const org = await this.orgService.getOrg(orgId);

        const sendInstantlyTransferEmailCallable = httpsCallable<
          ISendInstantlyTransferEmailReq,
          IHttpsCallableResponse
        >(this.functions, cloudFunctionNames.secureFileTransfer.sendInstantlyTransferEmail);

        const response = await sendInstantlyTransferEmailCallable({
          email,
          displayName,
          entityId,
          entityName: entity.legalName,
          orgName: org.name,
        });

        if (!response.data.success) {
          throw new Error(response.data.message);
        }
        break;
      }
      default:
        exhaustiveCheck(operation, "Invalid operation");
    }
  }

  public async getEntities(groupIds: Set<UUID>, orgId: UUID): Promise<IEntityMap> {
    const entitiesCollectionDoc = this.getProEntityCollectionDoc(orgId);
    const groupIdChunks = chunkArray(Array.from(groupIds), 10);

    const entitiesSnap = await Promise.all(
      groupIdChunks.map(chunk =>
        getDocs(
          query(
            entitiesCollectionDoc,
            where("groupId", "in", chunk)
          )
        )
      )
    );

    const entities = entitiesSnap.flatMap(snapshot =>
      snapshot.docs.map(doc => this.convertEntityFromCf(doc.data()))
    );
    return this.convertEntityArrayToMap(entities);
  }

  public async getEntitiesByIds(orgId: UUID, entityIds: Set<UUID>): Promise<IEntity[]> {
    const entitiesCollectionRef = this.getProEntityCollectionDoc(orgId);
    const entityIdChunks = chunkArray(Array.from(entityIds), 10);

    const entitiesSnap = await Promise.all(
      entityIdChunks.map(chunk =>
        getDocs(
          query(
            entitiesCollectionRef,
            where(documentId(), "in", chunk)
          )
        )
      )
    );

    return entitiesSnap.flatMap(snapshot =>
      snapshot.docs.map(doc => this.convertEntityFromCf(doc.data()))
    );
  }

  public async addEntityToGroup(
    orgId: UUID,
    entityId: UUID,
    entityLegalName: string,
    groupId: UUID
  ): Promise<IEntity> {
    await this.updateEntityGroup(orgId, entityId, groupId);

    await this.groupService.addEntityToGroup(
      orgId,
      entityId,
      entityLegalName,
      groupId
    );

    return this.getProEntity(orgId, entityId);
  }

  public async moveEntityToGroup(
    orgId: UUID,
    entityId: UUID,
    entityLegalName: string,
    fromGroupId: UUID,
    toGroupId: UUID
  ): Promise<IEntity> {
    await this.updateEntityGroup(orgId, entityId, toGroupId);

    await this.groupService.moveEntityToGroup(
      orgId,
      entityId,
      entityLegalName,
      fromGroupId,
      toGroupId
    );

    return this.getProEntity(orgId, entityId);
  }

  private async updateEntityGroup(
    orgId: UUID,
    entityId: UUID,
    groupId: UUID
  ): Promise<void> {
    const entityRef = this.getProEntityRef(orgId, entityId);
    const entityData = await this.getProEntity(orgId, entityId);

    if (entityData.groupId === groupId) {
      throw new Error("Entity already exists in group");
    }

    const group = await this.groupService.getGroup(orgId, groupId);

    return updateDoc(entityRef, {
      groupId,
      groupName: group.name
    });
  }

  public async getEntityOwners(orgId: UUID, entityId: UUID): Promise<IOwner[]> {
    const entityData = await this.getProEntity(orgId, entityId);
    const ownerPromises = Object.keys(entityData.ownerRelationships).map(ownerId => {
      return this.ownerService.getById(ownerId);
    });

    return Promise.all(ownerPromises);
  }

  public async getEntitiesByOwnerId(orgId: UUID, ownerId: UUID): Promise<IEntity[]> {
    const entitiesQuery = query(
      collection(this.firestore, "organization", orgId, "entityList"),
      where(`ownerRelationships.${ownerId}`, "!=", null)
    );

    const snapshot = await getDocs(entitiesQuery);
    return snapshot.docs.map(doc => doc.data() as IEntity);
  }

  public async addOwnerToEntity(
    orgId: UUID,
    entityId: UUID,
    ownerId: UUID,
    boiType: BOIType
  ): Promise<IEntity> {
    const entityRef = this.getProEntityRef(orgId, entityId);
    const entityData = await this.getProEntity(orgId, entityId);
    if (SecureProService.isOwnerPresentInEntity(entityData, ownerId)) {
      await this.addOwnerBoiType(entityRef, ownerId, boiType);
    } else {
      await this.createOwnerRelationship(entityRef, ownerId, boiType);
    }
    return this.getProEntity(orgId, entityId);
  }

  public async removeOwnerFromEntity(
    orgId: UUID,
    entityId: UUID,
    ownerId: UUID,
    boiType: BOIType
  ): Promise<IEntity> {
    const entityRef = this.getProEntityRef(orgId, entityId);
    const entityData = await this.getProEntity(orgId, entityId);

    if (!SecureProService.isOwnerPresentInEntity(entityData, ownerId)) {
      throw new Error("Owner not found in entity");
    }

    if (Object.keys(entityData.ownerRelationships[ownerId]).length === 2) {
      await this.removeOwnerBoiType(entityRef, ownerId, boiType);
    } else {
      await this.removeOwnerRelationship(entityRef, ownerId);
    }

    return this.getProEntity(orgId, entityId);
  }

  public async removeOwnerFromOrg(orgId: UUID, ownerId: UUID): Promise<IEntity[]> {
    const owner = await this.ownerService.getById(ownerId);
    const entityIds: UUID[] = owner.entityIds;
    const removeOwnerPromises = entityIds.map(async entityId => {
      const entityRef = this.getProEntityRef(orgId, entityId);
      const entityData = await this.getProEntity(orgId, entityId);

      if (!SecureProService.isOwnerPresentInEntity(entityData, ownerId)) {
        throw new Error("Owner not found in entity");
      }

      await this.removeOwnerRelationship(entityRef, ownerId);
      return this.getProEntity(orgId, entityId);
    });
    await this.ownerService.removeOrgEntities(ownerId, orgId);

    return Promise.all(removeOwnerPromises);
  }

  public async transferEntityToSecureFile(
    entityId: UUID,
    orgId: UUID,
    ownerId: UUID
  ): Promise<IEntity> {
    const entityData = await this.getProEntity(orgId, entityId);

    if (!SecureProService.isOwnerPresentInEntity(entityData, ownerId)) {
      throw new Error("Owner not found in entity");
    }

    const updateEntityOperation = this.createEntityOperation("updateEntity");

    await updateEntityOperation({
      ...entityData,
      status: "Handoff Email Sent",
      handedOff: true,
    });

    return this.getProEntity(orgId, entityId);
  }

  public async instantlyTransferEntityToSecureFile(
    entityId: UUID,
    orgId: UUID,
    userId: UUID,
  ): Promise<IEntity> {
    const entityData = await this.getProEntity(orgId, entityId);

    const updateEntityOperation = this.createEntityOperation("updateEntity");

    await updateEntityOperation({
      ...entityData,
      userIds: [...new Set([...entityData.userIds, userId])],
      status: "Handoff Awaiting Payment",
      handedOff: true,
    });

    return this.getProEntity(orgId, entityId);
  }

  public async revokeSecureFileInvitation(entityId: UUID, orgId: UUID): Promise<IEntity> {
    const revokeSecureFileInviteCallable = httpsCallable<
      IRevokeSecureFileInviteReq,
      IHttpsCallableResponse
    >(this.functions, cloudFunctionNames.secureFileTransfer.revokeSecureFileInvite);

    const revokeResponse = await revokeSecureFileInviteCallable({
      entityId,
      orgId,
    });

    if (!revokeResponse.data.success) {
      throw new Error(revokeResponse.data.message);
    }

    return this.getProEntity(orgId, entityId);
  }

  public async submitToFinCEN(
    entityId: UUID,
    orgId: UUID,
    consentData: IEntitySubmitConsent
  ): Promise<IEntity> {
    const entityData = await this.getProEntity(orgId, entityId);

    const shouldUseBot = await this.featureFlags.shouldUseBot();

    const enqueueResponse = shouldUseBot
      ? await this.boirFilingsService.enqueueWebBOIRFiling(entityId, orgId)
      : await this.boirFilingsService.enqueueBOIR(entityId, orgId, consentData);

    if (!enqueueResponse) {
      throw new Error("Error during submitting to FinCEN");
    }

    const updateEntityOperation = this.createEntityOperation("updateEntity");
    await updateEntityOperation({
      ...entityData,
      status: "e-Filing Pending",
      ...(shouldUseBot ? { consentData } : {}),
    });

    return this.getProEntity(orgId, entityId);
  }

  public async updateEntityBoirStatus(
    entityId: UUID,
    orgId: UUID,
    entityBoirStatusUpdate: IEntityBoirStatusUpdate
  ): Promise<void> {
    const entityData = await this.getProEntity(orgId, entityId);

    const updateEntityOperation = this.createEntityOperation("updateEntity");
    await updateEntityOperation({
      ...entityData,
      ...entityBoirStatusUpdate,
    });
  }

  public async updateShareTypes(
    orgId: UUID,
    entityId: UUID,
    shareTypeChanges: IShareTypeChanges
  ): Promise<IEntity> {
    const entity = await this.getProEntity(orgId, entityId);

    const deletedShareTypes = await this.handleShareTypesDeletion(
      entity,
      Object.keys(shareTypeChanges.deleted) as UUID[],
      orgId,
    );

    const shareTypeWithChangedUnitsIds = Object.values(shareTypeChanges.updated)
      .filter((updatedShareType): updatedShareType is IShareType => {
        const shareType = entity.capTable?.shareTypes?.[updatedShareType?.id as string];

        if (!updatedShareType || !shareType) return false;

        return shareType.units !== updatedShareType.units;
      })
      .map(shareType => shareType.id);

    await this.handleOwnershipClearing(
      entity,
      shareTypeWithChangedUnitsIds,
      orgId,
    )

    const updatedShareTypes: IShareTypeReference = {
      ...deletedShareTypes,
      ...shareTypeChanges.updated,
      ...shareTypeChanges.created,
    };

    const updateEntity = this.createEntityOperation("updateEntity");

    await updateEntity({
      ...entity,
      capTable: {
        shareTypes: updatedShareTypes,
      },
    });

    return this.getProEntity(orgId, entityId);
  }

  public async manageCapTableRecords(
    orgId: UUID,
    entityId: UUID,
    shareTypeId: UUID,
    shareRecordChanges: IShareRecordChanges,
  ): Promise<IEntity> {
    const entityRef = this.getProEntityRef(orgId, entityId);
    const entity = await this.getProEntity(orgId, entityId);

    if (!entity.capTable.shareTypes[shareTypeId]) {
      throw new Error(`Error adding capitalization table record: shareType with id: ${shareTypeId} not found`);
    }


    await updateDoc(entityRef, {
      ...shareRecordChanges,
    } as UpdateData<IEntityCf>);

    return this.getProEntity(orgId, entityId);
  }

  public async clearOwnershipOfCapTableRecords(
    entityId: UUID,
    records: { ownerId: UUID; shareTypeId: UUID }[],
    orgId: UUID,
  ): Promise<void> {
    const entity = await this.getProEntity(orgId, entityId);
    const entityRef = this.getProEntityRef(orgId, entityId);

    const updatePayload: Record<string, 0> = {};

    for (const { ownerId, shareTypeId } of records) {
      const exists = !!entity.ownerRelationships[ownerId]?.capTableDetails[shareTypeId];

      if (!exists) {
        throw new Error(`Error updating cap table record: record [${ownerId} -> ${shareTypeId}] not found`);
      }

      updatePayload[`ownerRelationships.${ownerId}.capTableDetails.${shareTypeId}.ownershipAmount`] = 0;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await updateDoc(entityRef as DocumentReference<any>, updatePayload);
  }

  public async removeCapTableRecords(
    entityId: UUID,
    records: { ownerId: UUID; shareTypeId: UUID }[],
    orgId: UUID,
  ): Promise<void> {
    const entity = await this.getProEntity(orgId, entityId);
    const entityRef = this.getProEntityRef(orgId, entityId);

    const updatePayload: Record<string, FieldValue> = {};

    for (const { ownerId, shareTypeId } of records) {
      const exists = !!entity.ownerRelationships[ownerId]?.capTableDetails[shareTypeId];

      if (!exists) {
        throw new Error(`Error removing cap table record: record [${ownerId} -> ${shareTypeId}] not found`);
      }

      updatePayload[`ownerRelationships.${ownerId}.capTableDetails.${shareTypeId}`] = deleteField();
    }

    await updateDoc(entityRef, updatePayload);
  }

  public async addDocumentToType(
    orgId: UUID,
    entityId: UUID,
    documentTypeId: UUID,
    documents: IEntityDocument[],
  ): Promise<IEntity> {
    const entity = await this.getProEntity(orgId, entityId);

    await Promise.all(
      documents.map(async (document) => {
        const updatedDocument = {
          ...document,
          relatesToEntities: Array.from(
            new Set([...document.relatesToEntities, entityId])
          ),
        } as IEntityDocument;

        return this.documentManagementService.updateDocument(updatedDocument);
      })
    )

    const updatedDocumentTypes = entity.documentTypes.map(docType => {
      return docType.id === documentTypeId
        ? {
          ...docType,
          documents: [
            ...docType.documents,
            ...documents.map(doc => doc.id),
          ],
        } : docType
    });

    return this.updateDocumentTypes(orgId, entityId, updatedDocumentTypes);
  }

  public async moveDocumentBetweenTypes(
    orgId: UUID,
    entityId: UUID,
    fromDocumentTypeId: UUID,
    toDocumentTypeId: UUID,
    documentId: UUID,
  ): Promise<IEntity> {
    const entity = await this.getProEntity(orgId, entityId);

    const updatedDocumentTypes = entity.documentTypes.map(docType => {
      switch (docType.id) {
        case fromDocumentTypeId:
          return {
            ...docType,
            documents: docType.documents.filter(docId => docId !== documentId),
          }
        case toDocumentTypeId:
          return {
          ...docType,
          documents: [
            ...docType.documents,
            documentId,
          ],
        }
        default:
          return docType
      }
    });

    return this.updateDocumentTypes(orgId, entityId, updatedDocumentTypes);
  }

  public async removeDocumentFromType(
    orgId: UUID,
    entityId: UUID,
    documentTypeId: UUID,
    document: IEntityDocument,
  ): Promise<IEntity> {
    const entity = await this.getProEntity(orgId, entityId);

    const updatedDocumentTypes = entity.documentTypes.map(docType => {
      return docType.id === documentTypeId
        ? {
          ...docType,
          documents: docType.documents.filter(docId => docId !== document.id),
        } : docType
    });

    await this.documentManagementService.deleteDocumentFromEntity(document.id, entityId);

    return this.updateDocumentTypes(orgId, entityId, updatedDocumentTypes);
  }

  public async updateDocumentTypes(
    orgId: UUID,
    entityId: UUID,
    updatedDocTypes: IDocumentType[]
  ): Promise<IEntity> {
    const entityRef = this.getProEntityRef(orgId, entityId);

    await updateDoc(entityRef, {
      documentTypes: updatedDocTypes
    })

    return this.getProEntity(orgId, entityId);
  }

  public getProEntityFirestoreObservable(
    orgId: Observable<UUID>,
    entity: Observable<IEntity>,
    entityUpdateFilter$: Observable<boolean>
  ): Observable<IEntity | null> {
    return combineLatest([orgId, entity]).pipe(
      distinctUntilChanged(
        ([/*orgId*/, previousEntity], [/*orgId*/, nextEntity]) => previousEntity.id === nextEntity.id
      ),
      switchMap(([orgId, entity]) => {
        const entityRef = this.getProEntityRef(orgId, entity.id)
        return this.getEntityFirestoreObservable(entityRef, entityUpdateFilter$)
      })
    )
  }
}
